import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {
  TablePagination,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Button,
  Icon,
  useTheme,
} from "@material-ui/core";
import {ArrowRightAlt, PlayArrow, SwapHoriz} from "@material-ui/icons";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";

import {
  MaskedText,
  DatePicker,
  FlightNumber,
  MaskedTime,
  LightTooltip,
  HtmlTooltip,
} from "../../../controls";
import { TermsConditions } from "../../../icons";
import SlotHistoryDialog from "../../../controls/FormDialog";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { getSeason } from "../../../functions/GetSeason";
import AirportLookup from "../../Airports/components/AirportsLookup";
import SlotsValidator from "../validators/SlotsValidator";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";

import { useSlots, useDeleteSlot } from "../../../services/slots";
import { useSeasons } from "../../../services/settings";
import ScrMessageView from "./ScrMessageView";
import GenerateSlotDialog from "./GenerateSlotDialog";
import SendSlotRequestDialog from "./SendSlotRequestDialog";
import MessageExchangeGrid from "./SlotMessageExchangeGrid";
import axios from 'axios';


const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const statusValues = [
  {"code":"REQUIRED","value":"Required"},
  {"code":"REQUESTED","value":"Requested"},
  {"code":"RECEIVED","value":"Confirmed"},
  {"code":"REJECTED","value":"Rejected"},
  {"code":"CANCELLED","value":"Cancelled"},
  {"code":"PENDING","value":"Pending"},
  {"code":"OFFER","value":"Offer"},
  {"code":"CONDITIONAL","value":"Conditional"},
];

const historyButtons = {
  cancel:{icon: "checkCircle", label: "OK", color: "primary"}
};

const slotIssues = [
  {"code":"inboundMissing","value":"Inbound flight on the {arrivalDate} no longer exists"},
  {"code":"inboundTimeChanged","value":"Arrival time of the inbound flight on the {arrivalDate} has changed to {arrivalTime}"},
  {"code":"inboundRouteChanged","value":"Origin of the inbound flight on the {arrivalDate} has changed to {origin}"},
  {"code":"outboundFlightChanged","value":"Outbound flight has changed to {outboundFlight.airlineCode}{outboundFlight.flightNo} {outboundFlight.flightDate} departing {outboundFlight.flightTime} UTC to {outboundFlight.destinationAirportCode}"},
  {"code":"outboundMissing","value":"Outbound flight on the {departureDate} no longer exists"},
  {"code":"outboundTimeChanged","value":"Departure time of the outbound flight on the {departureDate} has changed to {departureTime}"},
  {"code":"outboundRouteChanged","value":"Destination of the outbound flight on the {departureDate} has changed to {destination}"},
];

const apiSitePrefix = getApiSitePrefix();

const SlotReviewList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [generateSlot, setGenerateSlot] = useState({
    open: false,
    record: {}
  });
  const [sendSlot, setSendSlot] = useState({
    open: false,
    record: {}
  });

  const [slotMessageExchange, setSlotMessageExchange] = useState({
    showNoteDialog: false,
    record: null,
  });

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required === "review" || required === "missing")
    extraKeys.push(required);

  const slots = useSlots({tableRef, queryClient, extraKeys});
  const seasons = useSeasons({ });


  const refreshList = async () => {
    queryClient.invalidateQueries(["Slots"]);
  }

  const handleUpdateSlotClick = (rowData) => {
    const season = getSeason(seasons.data, rowData.slotPeriodFrom);

    let record = {
        ...rowData,
        arrivalTime:  rowData.scheduledArrivalTimeUTC ? moment.utc(rowData.scheduledArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm") : undefined,
        departureTime: rowData.scheduledDepartureTimeUTC ? moment.utc(rowData.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm") : undefined,
        newSlotMode: false,
        slotActionCode: 'C',
        parentRecord: {...rowData, offsetDays: moment.utc(rowData.departureDate, "YYYY-MM-DD").startOf('day').diff(moment.utc(rowData.arrivalDate, "YYYY-MM-DD").startOf('day'), 'day')},
        generalInfo: rowData.generalInfo?rowData.generalInfo:"REGARDS MAGMA AVIATION",
        season: season,
        slotDirection: rowData.slotDirection||'BOTH',
        slotChangePeriodFrom: rowData.slotPeriodFrom,
        slotChangePeriodTo: rowData.slotPeriodTo,
        changeDays: rowData.days,
        offsetDays: moment.utc(rowData.departureDate, "YYYY-MM-DD").startOf('day').diff(moment.utc(rowData.arrivalDate, "YYYY-MM-DD").startOf('day'), 'day')
    }
    if (rowData.outboundMissing || rowData.inboundMissing) {
      record['slotActionCode'] = 'D'
      record['arrivalTime'] = rowData.arrivalTime.substr(0, 5);
      record['departureTime'] = rowData.departureTime.substr(0, 5);
    }
    setGenerateSlot({...generateSlot, open: true, newSlot: false, record});
  }

  const handleIgnoreChangesClick = async (selectedRecord) => {
    SystemData.confirm(
      "Ignore Slot Review changes",
      `Are you sure you wish to ignore the flight changes that mismatch the airport slot, please note that the airport will not be advised of these changes.`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"primary"}}
      ],
      async (response) => {
        if (response) {
          try {
            SystemData.toggleLoadingProgress(true, "Ignoring changes");
            const response = await axios.put(`${apiSitePrefix}/slotReview/ignore`, selectedRecord);
            SystemData.toggleLoadingProgress(false);
            if (!response.data) {
              SystemData.alert("Error", `Could not ignore change. Server is not responding`);
            } else if (response.data.success) {
              queryClient.invalidateQueries(["Slots", 0, 0 , "review"]);
              queryClient.invalidateQueries(["Slots", "Count", 0, 0 , "review"]);
              queryClient.invalidateQueries(["Slots", 0, 0 , "missing"]);
              queryClient.invalidateQueries(["Slots", "Count", 0, 0 , "missing"]);
            } else {
              SystemData.alert("Error", `Could not ignore change. Server returned ${response.data.error}`);
            }
          } catch (err) {
            SystemData.toggleLoadingProgress(false);
            SystemData.alert("Error", `Could not ignore changes. Server is not responding`);
          }
        }
      }
    )
  }

  const getColumns = (props) => {
    const validatorColumn = ValidatorColumn(SlotsValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <div>
              {<span> {rowData.airportCode} </span>}
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Original Status",
        field: "status",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "REQUIRED",
        sorting: true,
        cellStyle: (value, rowData) => {
          const color = ['REQUIRED', 'REJECTED', 'OFFER', ].includes(value) && rowData.processStatus === "ACTIVE"? "red" : ['REQUESTED', 'CONDITIONAL', 'PENDING', ].includes(value) ? "orange" : "";
          const {cellStyle} = ColumnWidth(100);
          return ({...cellStyle, backgroundColor: color, textAlign: "center" });
        },
        render: rowData => (
          <span> { (!rowData.status) ? "" : __.find(statusValues, {code: rowData.status||"REQUESTED"}).value} </span>
          // <HtmlTooltip rowData={rowData} title={ <ScrMessageView slot={rowData} originalSlot={rowData.parentRecord?rowData.parentRecord:{}}/> } >
          //   <span> { (!rowData.status) ? "" : __.find(statusValues, {code: rowData.status||"REQUESTED"}).value} </span>
          // </HtmlTooltip>
        ),
        editComponent: (props) => {
          return (
            <Select
              value={props.value === null?'':props.value}
              onChange={
                (evt, data) => {
                  const comboValue = __.find(statusValues, {value: data.props.value});
                  props.onChange(comboValue.code);
                }
              }
              renderValue={(value) => {
                const comboValue = __.find(statusValues, {code: value});
                const valueToRender = (comboValue === undefined) ? "" : comboValue.value;
                return(`${valueToRender}`);
              }}
            >
              {(statusValues) ?
                statusValues.map((record) => {
                  return (
                    <MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>
                  );
                }) :
                null
              }
            </Select>
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(30)),
        title: "",
        field: "conditions",
        editable: "never",
        render: rowData => (
          <>
            {rowData.conditions ? <TermsConditions /> : <></>}
          </>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(40)),
        title: "",
        field: "slotType",
        editable: "never",
        emptyValue: "",
        initialEditValue: "",
        render: rowData => {
          return (
            <>
              {rowData.slotType === "TURNROUND" ? <SwapHoriz /> : <ArrowRightAlt />}
            </>
          );
        },
      },
      // {
      //   ...baseColumn,
      //   ...(ColumnWidth(125)),
      //   title: "Last Action",
      //   field: "slotActionCode",
      //   editable: "never",
      //   render: (rowData) => (
      //     <ComboSelect
      //       id="slotActionCode"
      //       label="Slot Request"
      //       group="SLOTS"
      //       subGroup="ALL ACTIONS"
      //       fullWidth
      //       value={`${rowData.slotActionCode}`}
      //       displayOnly={true}
      //     />
      //   ),
      // },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Requested On",
        field: "requestedTime",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.requestedTime).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.requestedTime ? moment.utc(rowData.requestedTime).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Reference",
        field: "slotReference",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData =>
          <HtmlTooltip rowData={rowData} title={ <ScrMessageView slot={rowData} originalSlot={rowData.parentRecord?rowData.parentRecord:{}}/> } >
            <span> {rowData.slotReference} </span>
          </HtmlTooltip>
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period From",
        field: "slotPeriodFrom",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.validFrom).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodFrom).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period To",
        field: "slotPeriodTo",
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodTo).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      // {
      //   ...baseColumn,
      //   ...(ColumnWidth(125)),
      //   title: "Season",
      //   field: "slotPeriodTo",
      //   editable: "never",
      //   render: rowData => <Season slotFrom={rowData.slotPeriodFrom} />
      // },
      {
        ...baseColumn,
        ...(ColumnWidth(65)),
        title: "Days",
        field: "days",
        editable: false,
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"D".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Inbound Flight",
        field: "arrivalAirlineCode",
        initialEditValue: "",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.arrivalAirlineCode} ${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.arrivalAirlineCode}-${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.arrivalAirlineCode}${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        editComponent: (props) => (
          <FlightNumber
            mask={"XX-9999"}
            code={props.rowData.arrivalAirlineCode}
            number={props.rowData.arrivalFlightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const flightNo = parseInt(e.number);
              let data = { ...props.rowData, arrivalAirlineCode: e.code, arrivalFlightNo: flightNo };
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <div>
            {props.arrivalAirlineCode}&nbsp;{props.arrivalFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Origin Airport",
        field: "originAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.originAirportCode} />}>
            <span> {rowData.originAirportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Arrival Time",
        field: "arrivalTime",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.arrivalTime?.substr(0,Math.min(rowData.arrivalTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Departure Time",
        field: "departureTime",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.departureTime?.substr(0,Math.min(rowData.departureTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Destination Airport",
        field: "destinationAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.destinationAirportCode} />}>
            <span> {rowData.destinationAirportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Outbound Flight",
        field: "departureAirlineCode",
        initialEditValue: "",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.departureAirlineCode} ${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.departureAirlineCode}-${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.departureAirlineCode}${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        editComponent: (props) => (
          <FlightNumber
            mask={"XX-9999"}
            code={props.rowData.departureAirlineCode}
            number={props.rowData.departureFlightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const flightNo = parseInt(e.number);
              let data = { ...props.rowData, departureAirlineCode: e.code, departureFlightNo: flightNo };
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <div>
            {props.departureAirlineCode}&nbsp;{props.departureFlightNo}
          </div>
        ),
      },
      // {
      //   ...baseColumn,
      //   ...(ColumnWidth(80, 2, 200)),
      //   title: "Aircraft",
      //   field: "aircraftTailNumber",
      //   editComponent: (props) => (
      //     <AircraftLookup
      //       value={props.value}
      //       onChange={props.onChange}
      //     />
      //   ),
      // },
      {
        ...baseColumn,
        title: "Slot Problem",
        field: "inboundMissing",
        initialEditValue: "",
        render: rowData => {
          const slotProblems = [];
          slotIssues.forEach(issue => {
            if (__.has(rowData, issue.code) && rowData[issue.code]) {
              const fields = {
                arrivalDate: moment.utc(rowData.arrivalDate, "YYYY-MM-DD").format('DD MMM'),
                departureDate: moment.utc(rowData.departureDate, "YYYY-MM-DD").format('DD MMM'),
                arrivalTime:  moment.utc(rowData.scheduledArrivalTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm"),
                departureTime:  moment.utc(rowData.scheduledDepartureTimeUTC, "YYYY-MM-DD HH:mm:ss").format("HH:mm"),
                origin: rowData.inboundOrigin,
                destination: rowData.outboundDestination,
              }
              let reasonText = issue.value;
              Object.entries(fields).forEach(([key, value]) => {
                reasonText = __.replace(reasonText, `{${key}}`, value);
              });
              if (rowData.newOutboundFlight) {
                Object.entries(rowData.newOutboundFlight).forEach(([key, value]) => {
                  const textValue = key === 'flightDate'?moment.utc(value, "YYYY-MM-DD").format('DD MMM'):value; 
                  reasonText = __.replace(reasonText, `{outboundFlight.${key}}`, textValue);
                });  
              }
              slotProblems.push(reasonText);
            }
          });
          return (<span> {slotProblems.join(", ")} </span>)
        }
      },
    ];
  };


  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Action: _props => {
            let props = {..._props};
            if (typeof props.action.action === "function") {
              props.action = {..._props.action, ..._props.action.action(_props.data)};
            }
            if (props.action.icon==="generate") {
              return (
                <Tooltip title={props.action.tooltip}>
                  <Button
                    style={{...props.action.style, height: 24, display: props.action.hidden?'none':'inherit'}}
                    variant="contained"
                    color="primary"
                    startIcon={props.action.startIcon||<PlayArrow/>}
                    className={classes.button}
                    disabled={ props.action.disableForStatus?props.action.disableForStatus.includes(props.data[0].status)||props.action.disabled:props.action.disabled}
                    onClick={() => {
                      props.action.onClick(props.data)
                    }}
                  >
                    {props.action.text}
                  </Button>
                </Tooltip>
              )
            } else if (props.action.icon) {
              if (props.action.tooltip) {
                return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
              }
              return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
            } else {
              return (<></>)
            }
          }
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          (rowData) => ({
            icon: "generate",
            text: "Ignore",
            tooltip: "Ignore changes made to the flight criteria since ordering.",
            onClick: handleIgnoreChangesClick,
            startIcon: <Icon>redo</Icon>,
            style: {width: 100},
          }),
          (rowData) => ({
            icon: "generate",
            text: "Amend",
            disabled: !state.canEdit,
            tooltip: "Amend Slot to resolve slot problem",
            onClick: handleUpdateSlotClick,
            startIcon: <Icon>edit</Icon>,
            style: {width: 100},
          }),
        ]}
        title="Slots Requiring Review"
        columns={getColumns({canEdit: state.canEdit})}
        data={slots.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />

      {generateSlot.open ?
        <GenerateSlotDialog
          record={generateSlot.record}
          canEdit={state.canEdit}
          close={()=>{setGenerateSlot({open: false, record: {}})}}
          send={(slotRequest, slotRecord, originalRecord)=>{
            setSendSlot({open: true, request: slotRequest, record: slotRecord, originalRecord: originalRecord});
            setGenerateSlot({open: false, record: {}});
          }}
          classes={classes}
          dialogStates={{showDialog: true}}
          newSlot={generateSlot.newSlot}
          seasons={seasons.data}
        />
        : <></>
      }
      {sendSlot.open ?
        <SendSlotRequestDialog
          request={sendSlot.request}
          record={sendSlot.record}
          originalRecord={sendSlot.originalRecord}
          canEdit={state.canEdit}
          close={()=>{setSendSlot({open: false, record: {}})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: sendSlot.record?.airport}}
        />
        : <></>
      }

      {slotMessageExchange.showNoteDialog
        ?
        <SlotHistoryDialog
          title="Slot Message Exchange"
          message=""
          open={slotMessageExchange.showNoteDialog}
          record={{notes: slotMessageExchange.rowData.messageExchange}}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          buttons={historyButtons}
          onChange={(result) => {
            setSlotMessageExchange({...slotMessageExchange, showNoteDialog: false, rowData: null});
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true}>
              <MessageExchangeGrid messages={slotMessageExchange.rowData.messageExchange}/>
            </Grid>
          </Grid>
        </SlotHistoryDialog>
        :
        <></>
      }

    </div>
  );


}

export default SlotReviewList;
